import clsx from 'clsx';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import LazyImgixImage from '@components/LazyImgixImage';
import type { ModalHeaderProps } from '@components/MarketingModal/common/modalHeader';
import { TiModalColorScheme } from '@components/TIModal';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';
import { getSafeUrl } from '@utils/safe_url';

export type SingleContentHighlightTextProps = ModalHeaderProps & {
  imageURL?: string;
  mainCTA?: string;
  mainURL?: string;
  modalTheme?: TiModalColorScheme;
  optionalCTA?: string;
  optionalURL?: string;
};

/**
 * Promotional modal for a single piece of content.
 * Growth calls this the Retention P1 LE Modal - Single Content Highlight Text.
 */
const SingleContentHighlightText = ({
  highlight,
  imageURL,
  mainCTA,
  mainURL,
  modalTheme,
  optionalCTA,
  optionalURL,
  subtitle,
  title,
}: SingleContentHighlightTextProps) => {
  const { trackEvent } = useEventTracking();

  if (!imageURL || !mainCTA || !mainURL) {
    return null;
  }

  const safeMainURL = getSafeUrl(mainURL, { allowRelative: true });
  const safeOptionalURL = getSafeUrl(optionalURL, { allowRelative: true });

  const handleMainCTAClick = () => {
    trackEvent(
      click({ clickTarget: ClickTarget.AnchorLink, clickText: mainCTA, clickLink: safeMainURL })
    );
  };

  const handleOptionalCTAClick = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.AnchorLink,
        clickText: optionalCTA,
        clickLink: safeOptionalURL,
      })
    );
  };

  const textColor = modalTheme === TiModalColorScheme.dark ? 'text-white' : 'text-primary-black';

  return (
    <div className="flex w-full flex-wrap items-center justify-center px-32 pb-32 pt-64 md:py-64 lg:px-0">
      <div className="flex max-w-full flex-wrap items-start md:max-w-[960px] md:flex-nowrap md:gap-x-48">
        <LazyImgixImage
          src={imageURL}
          wrapperClassName="w-full h-auto md:w-[400px] flex-shrink-0 overflow-hidden"
          className="aspect-[405/230] h-full w-full object-cover"
          role="presentation"
        />
        <div className="mt-8 flex flex-col md:mt-0">
          {highlight && (
            <span className="block font-suisse-intl-mono text-overline-sm font-normal text-primary-magenta sm:text-overline-md">
              {highlight}
            </span>
          )}
          <h1
            className={clsx(
              'my-0 font-suisse-works text-headline-sm sm:text-headline-lg',
              textColor
            )}
          >
            {title}
          </h1>
          <p
            className={clsx(
              'mb-0 mt-8 font-suisse-intl text-description-sm sm:text-body-lg',
              textColor
            )}
          >
            {ReactHtmlParser(subtitle)}
          </p>
          <div className="mt-24 flex flex-row items-center gap-x-32">
            <PrimaryButton
              as="link"
              className="my-0"
              href={safeMainURL}
              onClickCapture={handleMainCTAClick}
            >
              {mainCTA}
            </PrimaryButton>
            {optionalCTA && optionalURL && (
              <TextButton
                as="link"
                className="my-0"
                href={safeOptionalURL}
                onClickCapture={handleOptionalCTAClick}
              >
                {optionalCTA}
              </TextButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleContentHighlightText;
